import { updateDataNode } from 'src/endpoints'

import { StructureDetailDto } from '@/endpoints/models'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { RootState } from '@/store'
import { apiCallAction, AppDispatch, UpdateDeepPartial } from '@/store/utils'
import { assignCollectionOrder } from '@/utils/collectionOrder'
import { InitDataParams } from '@/utils/structureType/useStructureTypeActions'

import { inflateMappingPermission } from '../mapping/helpers'
import { loadNodeOrHistoryVersion } from '../node/utils'
import {
	API_NODE_INIT,
	API_NODE_SAVE,
	API_NODE_SELECT_TAB,
	API_NODE_UPDATE,
	API_NODE_UPDATE_GRAPH_DATA,
} from './constants'
import { ApiNodeDataForm, ApiNodeGraphData, ApiNodeTab } from './types'

interface InitApiNode {
	editMode: boolean
	force: boolean
	node: StructureDetailDto
	type: typeof API_NODE_INIT
}

interface UpdateApiNode {
	node: StructureDto
	type: typeof API_NODE_UPDATE
	update: UpdateDeepPartial<ApiNodeDataForm>
}

interface SaveApiNode {
	metadata: {
		node: StructureDto
	}
	payload: void
	type: typeof API_NODE_SAVE
}

interface SelectApiNodeTab {
	node: StructureDto
	tab: ApiNodeTab
	type: typeof API_NODE_SELECT_TAB
}

interface UpdateApiNodeGraphData {
	node: StructureDto
	type: typeof API_NODE_UPDATE_GRAPH_DATA
	update: UpdateDeepPartial<ApiNodeGraphData>
}

export const initApiNode =
	({ nodeId, version, editMode = false, force, envId }: InitDataParams) =>
	async (dispatch: AppDispatch) => {
		const node = await loadNodeOrHistoryVersion(nodeId, version, envId)

		dispatch({
			type: API_NODE_INIT,
			node,
			editMode,
			force,
		} as InitApiNode)
	}

export const saveApiNode =
	(node: StructureDto) =>
	async (dispatch: AppDispatch, getState: () => RootState) => {
		const apiNode = getState().apiNode.nodes[node.id]

		if (!apiNode) {
			throw new Error(`Saving unopened state ${JSON.stringify(node)}`)
		}

		const nonEmptyColumns = apiNode.form?.columns?.filter((x) => x.name) ?? []

		const formData = {
			...apiNode.form,
			columns: assignCollectionOrder(nonEmptyColumns),
			objectPermissions: assignCollectionOrder(
				(apiNode.form.objectPermissions || [])
					.filter((c) => !!c.userName)
					.map(inflateMappingPermission),
			),
		}

		await dispatch(
			apiCallAction<SaveApiNode>(
				() =>
					updateDataNode(node.id, {
						data: JSON.stringify(formData),
					}),
				API_NODE_SAVE,
				{ node },
			),
		)
	}

export const updateApiNode = (
	node: StructureDto,
	update: UpdateDeepPartial<ApiNodeDataForm>,
): Actions => ({
	type: API_NODE_UPDATE,
	node,
	update,
})

export const selectApiNodeTab = (
	node: StructureDto,
	tab: ApiNodeTab,
): Actions => ({
	type: API_NODE_SELECT_TAB,
	node,
	tab,
})

export type Actions =
	| InitApiNode
	| SaveApiNode
	| UpdateApiNode
	| UpdateApiNodeGraphData
	| SelectApiNodeTab
